'use client';

import { useMemo } from 'react';

import { ChatApi } from '@ps/api/chat';
import { withClientAPI } from '@ps/functions/utils/api';
import { nanoid } from 'nanoid';

import { prepareMessageContent } from '../functions/prepareMessageContent';
import { privateMessageEventDataToChatMessage } from '../functions/privateMessageEventDataToChatMessage';
import {
  type ChatMessage,
  ChatMessageType,
  type ClientChatMessage,
  type ImageContent,
  MsgSendStatus,
  type OrderContent,
  type ProductContent,
  type TextContent,
  type VideoContent,
} from '../types/ChatMessage';
import type { ChatSessionType } from '../types/ChatSessionType';
import type { ChatUser } from '../types/ChatUser';
import { useChatContext } from './useChatContext';
import { useGlobalChatContext } from './useGlobalChat';

const kLimit = 100;

export type MessageToSendPayloadTypes = {
  [ChatMessageType.Text]: TextContent;
  [ChatMessageType.Picture]: ImageContent;
  [ChatMessageType.Order]: OrderContent;
  [ChatMessageType.Product]: ProductContent;
  [ChatMessageType.SystemMessage]: never;
  [ChatMessageType.Video]: VideoContent;
  [ChatMessageType.Live]: never;
  [ChatMessageType.Post]: never;
};

export type MessageToSendBase<T extends ChatMessageType> = {
  sessionType: ChatSessionType;
  type: T;
  from: ChatUser;
  to: ChatUser;
  payload: MessageToSendPayloadTypes[T];
  quoted: ChatMessage | undefined;
  clientId?: string;
};

export type TextMessageToSend = MessageToSendBase<ChatMessageType.Text>;
export type PictureMessageToSend = MessageToSendBase<ChatMessageType.Picture>;
export type OrderMessageToSend = MessageToSendBase<ChatMessageType.Order>;
export type ProductMessageToSend = MessageToSendBase<ChatMessageType.Product>;

export type MessageToSend =
  | TextMessageToSend
  | PictureMessageToSend
  | OrderMessageToSend
  | ProductMessageToSend;

export type ChatMessageAPI = ReturnType<typeof useChatMessageAPI>;

/**
 * Per-room API: fetch history + send messages. Wires server events
 * (`messages-loaded`, `sending-message`, `send-message-success`,
 * `send-message-failed`) into the global `events$` subject so the
 * pending-messages manager + the chat-message context can react.
 *
 * Text, Picture, Video and Order messages are supported: the optimistic
 * bubble is emitted first, then `prepareMessageContent` uploads any
 * locally-picked media before the wire payload is built.
 */
export function useChatMessageAPI() {
  const { events$ } = useGlobalChatContext();
  const { targetUserId, roomId } = useChatContext();

  return useMemo(() => {
    const api = withClientAPI(ChatApi);

    const fetchMessages = async (
      sessionType: ChatSessionType,
      params?: {
        lastMessageId?: number;
        limit?: number;
        source?: string;
      },
    ): Promise<ChatMessage[]> => {
      const res = await api.chatControllerGetMessages({
        sessionType,
        toUserId: targetUserId,
        latestMessageId: params?.lastMessageId,
        cursor: 0,
        limit: params?.limit ?? kLimit,
      });
      const messages = res.map(x => privateMessageEventDataToChatMessage(x));
      events$.next({
        type: 'messages-loaded',
        messages: messages.slice(),
        source: params?.source,
      });
      return messages;
    };

    const sendMessage = async <T extends ChatMessageType>(
      message: MessageToSendBase<T>,
    ): Promise<void> => {
      const msg = clientChatMessageFromMessageToSend(
        roomId,
        message as MessageToSend,
      );
      events$.next({ type: 'sending-message', message: msg });

      try {
        // Upload any locally-picked media, then serialize the resulting
        // remote-URL content. The optimistic bubble above keeps showing the
        // local preview until the gateway echo swaps in the server message.
        const wireContent = await prepareMessageContent(msg);
        await api.chatControllerSendMessage({
          SendChatMessage: {
            toUserId: msg.to!.userId,
            clientId: msg.clientId,
            sessionType: msg.sessionType,
            type: msg.type,
            text:
              msg.type === ChatMessageType.Text
                ? (msg.content as TextContent).text
                : '',
            content: JSON.stringify(wireContent),
            ext: '',
            quoteMsg: msg.quoteMsg ? JSON.stringify(msg.quoteMsg) : '',
          },
        });
        events$.next({ type: 'send-message-success', message: msg });
      } catch (e) {
        console.error('send message error', e);
        events$.next({ type: 'send-message-failed', message: msg, error: e });
        throw e;
      }
    };

    const resendMessage = async (message: ClientChatMessage): Promise<void> => {
      const msgToSend = clientChatMessageToMessageToSend(message);
      await sendMessage(msgToSend);
    };

    return { fetchMessages, sendMessage, resendMessage };
  }, [events$, roomId, targetUserId]);
}

function clientChatMessageFromMessageToSend(
  roomId: string,
  message: MessageToSend,
): ClientChatMessage {
  return {
    sessionType: message.sessionType,
    messageId: -1,
    roomId,
    clientId: message.clientId ?? nanoid(32),
    from: message.from,
    to: message.to,
    type: message.type,
    text:
      message.type === ChatMessageType.Text
        ? (message.payload as TextContent).text
        : '',
    content: message.payload,
    time: Date.now(),
    sendStatus: MsgSendStatus.sending,
    lastSendTime: Date.now(),
    quoteMsg: message.quoted,
  } as ClientChatMessage;
}

function clientChatMessageToMessageToSend(
  message: ClientChatMessage,
): MessageToSend {
  // The runtime invariant — `content` matches the `type` discriminator —
  // is guaranteed by the producer (`clientChatMessageFromMessageToSend`).
  // TypeScript can't follow the cross-property narrowing across the
  // discriminated union, so we assert with a single cast at the return.
  return {
    sessionType: message.sessionType,
    type: message.type,
    from: message.from,
    to: message.to!,
    payload: message.content,
    quoted: message.quoteMsg,
    clientId: message.clientId,
  } as MessageToSend;
}
